/**
 * Design Tokens - Colors
 * Exported from Figma: Enersee Platform 2.0 - _Foundation_DesignSystem
 *
 * These tokens match the Figma design system and should be used consistently
 * across the application to ensure design-development parity.
 */

export const colorTokens = {
  // Base Colors
  base: {
    black: '#000000',
    white: '#ffffff',
  },

  // Text Colors
  text: {
    primary: '#1e1827', // 900
    'primary-on-brand': '#ffffff',
    secondary: '#474151', // 700
    'secondary-hover': '#2c2537',
    'secondary-on-brand': '#e4d9fb',
    tertiary: '#595362', // 600
    'tertiary-hover': '#474151',
    'tertiary-on-brand': '#e4d9fb',
    quaternary: '#777180', // 500
    'quaternary-on-brand': '#d0bcf6',
    white: '#ffffff',
    disabled: '#777180',
    placeholder: '#777180',
    'placeholder-subtle': '#d7d5da',

    // Brand text colors
    'brand-primary': '#522b89', // 900
    'brand-secondary': '#6934bc', // 700
    'brand-secondary-hover': '#5a2f9a',
    'brand-tertiary': '#7d46d7', // 600
    'brand-tertiary-alt': '#7d46d7',

    // Utility text colors
    'error-primary': '#d92d20', // 600
    'warning-primary': '#dc6803', // 600
    'success-primary': '#079455', // 600
  },

  // Foreground Colors
  foreground: {
    primary: '#1e1827', // 900
    secondary: '#474151', // 700
    'secondary-hover': '#2c2537',
    tertiary: '#595362', // 600
    'tertiary-hover': '#474151',
    quaternary: '#a8a4ae', // 400
    'quaternary-hover': '#777180',
    white: '#ffffff',
    disabled: '#a8a4ae',
    'disabled-subtle': '#d7d5da',

    // Brand foreground colors
    'brand-primary': '#7d46d7', // 600
    'brand-primary-alt': '#7d46d7',
    'brand-secondary': '#9566e6', // 500
    'brand-secondary-alt': '#9566e6',

    // Utility foreground colors
    'error-primary': '#d92d20',
    'error-secondary': '#f04438',
    'warning-primary': '#dc6803',
    'warning-secondary': '#f79009',
    'success-primary': '#079455',
    'success-secondary': '#17b26a',
  },

  // Background Colors
  background: {
    primary: '#ffffff',
    'primary-alt': '#ffffff',
    'primary-hover': '#fafafa',
    'primary-solid': '#0d0a12',
    secondary: '#fafafa',
    'secondary-alt': '#fafafa',
    'secondary-hover': '#f5f5f5',
    'secondary-subtle': '#fdfdfd',
    'secondary-solid': '#595362',
    tertiary: '#f5f5f5',
    quaternary: '#eae9eb',
    active: '#fafafa',
    disabled: '#f5f5f5',
    'disabled-subtle': '#fafafa',
    overlay: '#0d0a12',

    // Brand background colors
    'brand-primary': '#f9f6fe',
    'brand-primary-alt': '#f9f6fe',
    'brand-secondary': '#f0eafd',
    'brand-solid': '#7d46d7',
    'brand-solid-hover': '#6934bc',
    'brand-section': '#5a2f9a',
    'brand-section-subtle': '#6934bc',

    // Utility background colors
    'error-primary': '#fef3f2',
    'error-secondary': '#fee4e2',
    'error-solid': '#d92d20',
    'error-solid-hover': '#b42318',
    'warning-primary': '#fffaeb',
    'warning-secondary': '#fef0c7',
    'warning-solid': '#dc6803',
    'success-primary': '#ecfdf3',
    'success-secondary': '#dcfae6',
    'success-solid': '#079455',
  },

  // Border Colors
  border: {
    primary: '#d7d5da',
    secondary: '#eae9eb',
    'secondary-alt': '#00000014',
    tertiary: '#f5f5f5',
    disabled: '#d7d5da',
    'disabled-subtle': '#eae9eb',
    brand: '#9566e6',
    'brand-alt': '#7d46d7',
    error: '#f04438',
    'error-subtle': '#fda29b',
  },

  // Component Utility Colors
  component: {
    utility: {
      success: {
        50: '#ecfdf3',
        200: '#abefc6',
        700: '#067647',
      },
    },
  },
} as const;

export type ColorTokens = typeof colorTokens;
